import { useState, useMemo, useEffect, useRef, Fragment } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Circle, Polyline, Polygon } from 'react-leaflet';
import { useSearchParams } from 'react-router-dom';
import { useRealtimeStore, Location } from '../store/realtimeStore';
import { useUnitStore } from '../store/unitStore';
import { useAuthStore } from '../store/authStore';
import { useSidebar } from '../contexts/SidebarContext';
import { useQuery } from '@tanstack/react-query';
import { geofenceApi, locationApi } from '../services/api';
import clsx from 'clsx';
import { OffScreenIndicators } from '../components/OffScreenIndicators';
import TargetsLayer from '../components/map/TargetsLayer';
import FarmbaseLayer from '../components/map/FarmbaseLayer';
import IntentArrows from '../components/map/IntentArrows';
import { useOperationalMap } from '../components/map/useOperationalMap';
import { geometryPoints, normalizeGeometry } from '../lib/geo';
import BattleOverlay from '../components/map/BattleOverlay';
import MgrsReadout from '../components/map/MgrsReadout';
import Rangefinder from '../components/map/Rangefinder';
import PlatoonsPanel from '../components/map/PlatoonsPanel';
import AlertsPanel, { alertsPanelCount } from '../components/map/AlertsPanel';
import MapLegend from '../components/map/MapLegend';
import MapFilterPanel from '../components/map/MapFilterPanel';
import MapTypeSelector from '../components/map/MapTypeSelector';
import BrigadeCrest from '../components/map/BrigadeCrest';
import PlatoonMarker, { MovementTrail } from '../components/map/PlatoonMarker';
import PlatoonDetailPanel from '../components/map/PlatoonDetailPanel';
import {
  MarchingPolygon,
  MapCenterControl,
  FlyToLocation,
  SetInitialView,
  InvalidateMapSizeOnSidebar,
  OperationalAutoFit,
  RefitOnSidebarChange,
} from '../components/map/mapControls';
import BottomSheet from '../components/responsive/BottomSheet';
import { useIsMobile } from '../components/responsive/useMediaQuery';
import {
  DEFAULT_MAP_CENTER,
  DEFAULT_MAP_ZOOM,
  GEOFENCE_TYPE_COLORS,
  GEOFENCE_EMOJI,
  FIELD_PIN_TYPES,
  MAP_TILES,
  hexToRgba,
  parsePolygonCoords,
  getPlatoon,
  createGeofencePinIcon,
  type MapType,
} from '../components/map/mapUtils';
import { FunnelIcon } from '@heroicons/react/24/outline';

export default function LiveMapPage() {
  const { isOpen: isSidebarOpen } = useSidebar();
  const [searchParams, setSearchParams] = useSearchParams();
  const { locations, sosEvents, alerts, notifications, selectLocation, selectedLocation } = useRealtimeStore();

  const { getEffectiveUnitIds, units, fetchUnits, isHQ } = useUnitStore();
  const { user } = useAuthStore();

  // Fetch units on mount to get colors/roles.
  useEffect(() => {
    if (units.length === 0) fetchUnits();
  }, [units.length, fetchUnits]);

  const effectiveUnitIds = getEffectiveUnitIds(user?.unitId || null);
  const userIsHQ = isHQ(user?.unitId || null);
  const unitIdForApi = userIsHQ
    ? effectiveUnitIds && effectiveUnitIds.length === 1
      ? effectiveUnitIds[0]
      : undefined
    : user?.unitId || undefined;

  // REST seed + polling safety net for live markers.
  // The map renders from the socket-fed realtime store; if the socket drops or its token
  // goes stale, no markers appear. Seed the SAME store from GET /locations/live so platoons
  // still paint (initial load) and stay fresh (15s poll + on window focus). This is additive:
  // the socket remains the low-latency path; REST is the fallback.
  useQuery({
    queryKey: ['live-locations-seed'],
    queryFn: async () => {
      const res = await locationApi.getLive();
      const raw: any[] = res.data?.locations || [];
      // Normalize to the IDENTICAL shape useWebSocket handleCurrentState produces so
      // markers render the same whether fed by socket or REST (same derived fields).
      const normalized = raw.map((loc: any) => {
        const platoon = loc.platoon || loc.device?.platoon;
        return {
          ...loc,
          unitId: loc.unitId || platoon?.unitId,
          platoonId: loc.platoonId || platoon?.id,
          platoon,
          device: {
            ...(loc.device || {}),
            id: (loc.device?.id || loc.deviceId || '') as string,
            platoon,
          },
        };
      });
      // Unit-scope filtering is applied at render time (locationArray below), so seed
      // the store unscoped and let the render layer decide visibility.
      useRealtimeStore.getState().setLocations(normalized);
      return raw;
    },
    refetchInterval: 15000,
    refetchOnWindowFocus: true,
  });

  // Filter alerts by unit.
  const filteredAlerts = useMemo(() => {
    if (!effectiveUnitIds || effectiveUnitIds.length === 0) return alerts;
    return alerts.filter((alert) => !!alert.unitId && effectiveUnitIds.includes(alert.unitId));
  }, [alerts, effectiveUnitIds]);

  // Recent geofence events.
  const { data: geofenceEventsData } = useQuery({
    queryKey: ['geofence-events-recent', effectiveUnitIds],
    queryFn: () => geofenceApi.getRecentEvents({ limit: 50, unitId: unitIdForApi }),
    refetchInterval: 10000,
  });

  const geofenceEvents = geofenceEventsData?.data?.events || [];

  const filteredGeofenceEvents = useMemo(() => {
    if (!effectiveUnitIds || effectiveUnitIds.length === 0) return geofenceEvents;
    return geofenceEvents.filter((event: any) => {
      const platoonUnit = event.platoon?.unitId || event.platoon?.unit?.id;
      if (platoonUnit) return effectiveUnitIds.includes(platoonUnit);
      if (event.geofence?.unitId) return effectiveUnitIds.includes(event.geofence.unitId);
      return false;
    });
  }, [geofenceEvents, effectiveUnitIds]);

  const [showFilters, setShowFilters] = useState(false);
  // Mobile bottom-sheet selector (one sheet at a time; null = all closed).
  const [mobileSheet, setMobileSheet] = useState<null | 'filters' | 'platoons' | 'alerts' | 'legend'>(null);
  const [mapType, setMapType] = useState<MapType>('street');
  const [filters, setFilters] = useState({
    showOnline: true,
    showOffline: true,
    showSOS: true,
    showGeofences: true,
    showTrails: true,
    showFarmbases: true,
    showTargets: true,
    showInactiveTargets: false,
  });

  // Operational picture (F-1/F-2): unit-scoped farmbases + targets feed the
  // overlay layers AND the auto-fit bounds.
  const { farmbases, targets } = useOperationalMap({
    showInactiveTargets: filters.showInactiveTargets,
  });

  // F-6: latched once the operator pans/zooms; reset by the Recenter button.
  const userInteractedRef = useRef(false);

  // On desktop the Platoons/Alerts stack (w-80 + right-4 margins) floats OVER
  // the map — pad fits so fitted elements stay visible left of it.
  const isMobile = useIsMobile();
  const fitPadBottomRight = useMemo<[number, number]>(
    () => (isMobile ? [60, 60] : [60 + 336, 60]),
    [isMobile]
  );

  // Platoon travel-path (history) tracking.
  const [selectedPlatoonId, setSelectedPlatoonId] = useState<string | null>(null);
  const [platoonPath, setPlatoonPath] = useState<[number, number][] | null>(null);
  const [pathLoading, setPathLoading] = useState(false);

  const fetchPlatoonPath = async (platoonId: string) => {
    setPathLoading(true);
    try {
      const endTime = new Date();
      const startTime = new Date(endTime.getTime() - 24 * 60 * 60 * 1000); // last 24h

      const response = await locationApi.getPlatoonHistory(platoonId, {
        startTime: startTime.toISOString(),
        endTime: endTime.toISOString(),
      });

      const history = response.data?.history || [];
      if (history.length > 0) {
        const path: [number, number][] = history
          .sort((a: any, b: any) => new Date(a.deviceTime).getTime() - new Date(b.deviceTime).getTime())
          .map((loc: any) => [loc.latitude, loc.longitude]);
        setPlatoonPath(path);
        setSelectedPlatoonId(platoonId);
      } else {
        setPlatoonPath(null);
        setSelectedPlatoonId(null);
      }
    } catch (error) {
      console.error('Failed to fetch platoon path:', error);
      setPlatoonPath(null);
      setSelectedPlatoonId(null);
    }
    setPathLoading(false);
  };

  const clearPlatoonPath = () => {
    setPlatoonPath(null);
    setSelectedPlatoonId(null);
  };

  // URL params for initial position.
  const urlLat = searchParams.get('lat');
  const urlLon = searchParams.get('lon');
  const urlZoom = searchParams.get('zoom');
  const urlName = searchParams.get('name');

  // Clear URL params after initial load (keeps URL clean).
  useEffect(() => {
    if (urlLat || urlLon || urlZoom || urlName) {
      const timer = setTimeout(() => setSearchParams({}), 2000);
      return () => clearTimeout(timer);
    }
  }, []);

  // Fetch geofences (backend applies unit scope for unit users).
  const { data: geofencesData } = useQuery({
    queryKey: ['geofences-map', effectiveUnitIds],
    queryFn: () => geofenceApi.getAll({ isActive: true, unitId: unitIdForApi }),
    staleTime: 0,
  });

  const allGeofences = geofencesData?.data?.geofences || [];

  const geofences = useMemo(() => {
    if (user?.unitId) return allGeofences; // backend already scoped
    if (!effectiveUnitIds || effectiveUnitIds.length === 0) return allGeofences;
    return allGeofences.filter((g: any) => effectiveUnitIds.includes(g.unitId));
  }, [allGeofences, effectiveUnitIds, user?.unitId]);

  // A player-unit user tied to a single platoon sees only their own platoon.
  const userUnitRole = useMemo(() => {
    if (!user?.unitId) return null;
    return units.find((x) => x.id === user.unitId)?.role ?? null;
  }, [user?.unitId, units]);
  const isUserHQ = !user?.unitId || userUnitRole === 'HQ';
  const restrictToSelf = !!(user?.platoonId && !isUserHQ);

  // Unit-scope filtering, based on the platoon/location unitId (no PII rules).
  const locationArray = useMemo(() => {
    return Array.from(locations.values()).filter((loc) => {
      const platoon = getPlatoon(loc);
      const unitId = loc.unitId || platoon?.unitId;

      if (effectiveUnitIds) {
        if (!unitId || !effectiveUnitIds.includes(unitId)) return false;
      } else if (user?.unitId && !isUserHQ) {
        // Self-scope only applies to NON-HQ users. An HQ commander bound to the
        // HQ unit must see every platoon, not just (nonexistent) HQ-unit ones.
        if (unitId !== user.unitId) return false;
      }

      if (restrictToSelf) {
        const pid = loc.platoonId || platoon?.id;
        if (pid && pid !== user?.platoonId) return false;
      }

      return true;
    });
  }, [locations, effectiveUnitIds, user, restrictToSelf, isUserHQ]);

  // Deduplicate by platoon (most recent) + apply online/offline/SOS filters.
  const filteredLocations = useMemo(() => {
    const platoonMap = new Map<string, Location>();
    const orphans: Location[] = [];

    locationArray.forEach((loc) => {
      const platoon = getPlatoon(loc);
      const platoonId = loc.platoonId || platoon?.id;
      const isOnline = new Date(loc.serverTime).getTime() > Date.now() - 5 * 60 * 1000;
      const hasSOS =
        !!platoon?.sos ||
        sosEvents.some(
          (sos) =>
            !!platoonId &&
            sos.platoonId === platoonId &&
            !!sos.status &&
            ['TRIGGERED', 'ACKNOWLEDGED', 'RESPONDING'].includes(sos.status)
        );

      let shouldShow = false;
      if (hasSOS && filters.showSOS) shouldShow = true;
      else if (isOnline && filters.showOnline) shouldShow = true;
      else if (!isOnline && filters.showOffline) shouldShow = true;

      if (!shouldShow) return;

      if (platoonId) {
        const existing = platoonMap.get(platoonId);
        if (!existing || new Date(loc.serverTime) > new Date(existing.serverTime)) {
          platoonMap.set(platoonId, loc);
        }
      } else {
        orphans.push(loc);
      }
    });

    return [...Array.from(platoonMap.values()), ...orphans];
  }, [locationArray, filters, sosEvents]);

  // Online / offline platoon counts.
  const platoonCounts = useMemo(() => {
    const platoonMap = new Map<string, Location>();
    locationArray.forEach((loc) => {
      const platoon = getPlatoon(loc);
      const platoonId = loc.platoonId || platoon?.id;
      if (platoonId) {
        const existing = platoonMap.get(platoonId);
        if (!existing || new Date(loc.serverTime) > new Date(existing.serverTime)) {
          platoonMap.set(platoonId, loc);
        }
      }
    });

    let onlineCount = 0;
    let offlineCount = 0;
    Array.from(platoonMap.values()).forEach((loc) => {
      const isOnline = new Date(loc.serverTime).getTime() > Date.now() - 5 * 60 * 1000;
      if (isOnline) onlineCount++;
      else offlineCount++;
    });

    return { onlineCount, offlineCount, total: onlineCount + offlineCount };
  }, [locationArray]);

  const filteredSOSEvents = useMemo(() => {
    if (!effectiveUnitIds) return sosEvents;
    return sosEvents.filter((sos) => effectiveUnitIds.includes(sos.unitId || ''));
  }, [sosEvents, effectiveUnitIds]);

  // Badge count for the mobile Alerts chip/sheet.
  const mobileAlertCount = alertsPanelCount({
    sosEvents: filteredSOSEvents,
    alerts: filteredAlerts,
    notifications,
    geofenceEvents: filteredGeofenceEvents,
  });

  const selectedLoc = selectedLocation ? locations.get(selectedLocation) : null;
  const flyToPosition = selectedLoc ? ([selectedLoc.latitude, selectedLoc.longitude] as [number, number]) : null;
  const selectedPlatoonKey = selectedLoc ? selectedLoc.platoonId || getPlatoon(selectedLoc)?.id || null : null;

  // Planned geofences (FARMBASE / BOUNDARY) drive stable auto-fit bounds.
  const planningGeofences = useMemo(
    () => geofences.filter((g: any) => g.type === 'FARMBASE' || g.type === 'BOUNDARY'),
    [geofences]
  );
  const planningPoints = useMemo(() => {
    const pts: [number, number][] = [];
    planningGeofences.forEach((g: any) => {
      if (g.polygonCoords) pts.push(...parsePolygonCoords(g.polygonCoords));
      else if (typeof g.centerLat === 'number' && typeof g.centerLon === 'number') pts.push([g.centerLat, g.centerLon]);
    });
    return pts;
  }, [planningGeofences]);
  const planningKey = useMemo(
    () => planningGeofences.map((g: any) => g.id).sort().join(','),
    [planningGeofences]
  );

  // F-6: bounds over ALL operational elements (platoon markers + farmbase
  // geometry + target geometry).
  const operationalPoints = useMemo(() => {
    const pts: [number, number][] = [];
    filteredLocations.forEach((loc) => pts.push([loc.latitude, loc.longitude]));
    farmbases.forEach((fb) =>
      geometryPoints(normalizeGeometry(fb)).forEach((p) => pts.push([p.lat, p.lon]))
    );
    targets.forEach((t) =>
      geometryPoints(normalizeGeometry(t)).forEach((p) => pts.push([p.lat, p.lon]))
    );
    return pts;
  }, [filteredLocations, farmbases, targets]);

  // Stable identity of the operational element SET — auto-fit re-runs only when
  // an element appears/disappears, not on every position tick.
  const operationalKey = useMemo(() => {
    const ids = [
      ...filteredLocations.map((l) => `p:${l.platoonId || getPlatoon(l)?.id || l.deviceId}`),
      ...farmbases.map((f) => `f:${f.id}`),
      ...targets.map((t) => `t:${t.id}`),
    ];
    return ids.sort().join(',');
  }, [filteredLocations, farmbases, targets]);

  // Recenter button target: ALL operational elements + planned geofences.
  const fitPoints = useMemo(
    () => [...operationalPoints, ...planningPoints],
    [operationalPoints, planningPoints]
  );

  const mapCenter = useMemo((): [number, number] => {
    if (urlLat && urlLon) return [parseFloat(urlLat), parseFloat(urlLon)];
    if (planningPoints.length > 0) {
      const lats = planningPoints.map((p) => p[0]);
      const lons = planningPoints.map((p) => p[1]);
      return [(Math.min(...lats) + Math.max(...lats)) / 2, (Math.min(...lons) + Math.max(...lons)) / 2];
    }
    return DEFAULT_MAP_CENTER;
  }, [urlLat, urlLon, planningPoints]);

  const calculatedZoom = urlZoom ? parseInt(urlZoom) : DEFAULT_MAP_ZOOM;

  return (
    // Fills the content area; full-bleed on mobile (negative margins escape
    // the main padding), inset card with border on desktop.
    <div className="relative flex-1 min-h-[320px] -m-3 sm:-m-4 lg:m-0">
      {/* Map Container - Full width, extends behind widgets */}
      <div className="absolute inset-0 overflow-hidden lg:rounded-xl lg:border border-gray-700">
        <MapContainer
          center={mapCenter}
          zoom={calculatedZoom}
          className="h-full w-full"
          style={{ height: '100%', width: '100%' }}
          zoomControl={false}
          key={`map-${effectiveUnitIds?.join('-') || 'all'}`}
        >
          <TileLayer
            key={mapType}
            attribution={MAP_TILES[mapType].attribution}
            url={MAP_TILES[mapType].url}
            subdomains={MAP_TILES[mapType].subdomains}
            maxZoom={20}
          />

          <InvalidateMapSizeOnSidebar sidebarOpen={isSidebarOpen} />
          <SetInitialView center={mapCenter} zoom={calculatedZoom} />

          {/* F-6: auto-fit over the full operational picture (falls back to
              planned geofences until live data arrives). */}
          <OperationalAutoFit
            points={operationalPoints}
            fitKey={operationalKey || planningKey}
            fallbackPoints={planningPoints}
            userInteractedRef={userInteractedRef}
            paddingBottomRight={fitPadBottomRight}
          />
          <RefitOnSidebarChange
            sidebarOpen={isSidebarOpen}
            points={fitPoints}
            userInteractedRef={userInteractedRef}
          />


          <FlyToLocation position={flyToPosition} />

          {/* Geofence layers */}
          {filters.showGeofences &&
            geofences.map((geofence: any) => {
              const type = geofence.type as string;
              let color = GEOFENCE_TYPE_COLORS[type] || '#0ea5e9';
              if (type === 'BOUNDARY' && geofence.unitId) {
                const u = units.find((b) => b.id === geofence.unitId);
                if (u?.aorColor) color = u.aorColor;
              }

              // Polygon geofences (FARMBASE/TARGET/BOUNDARY/CHECKPOINT drawn as areas).
              if (geofence.polygonCoords) {
                const positions = parsePolygonCoords(geofence.polygonCoords);
                if (positions.length < 3) return null;

                const popup = (
                  <Popup>
                    <div className="font-semibold">{geofence.name}</div>
                    <div className="text-xs text-gray-500">{type}</div>
                    {geofence.description && <div className="text-xs text-gray-600 mt-1">{geofence.description}</div>}
                  </Popup>
                );

                if (type === 'BOUNDARY') {
                  return (
                    <Fragment key={geofence.id}>
                      <MarchingPolygon
                        positions={positions}
                        pathOptions={{
                          color: hexToRgba(color, 0.5),
                          fillColor: 'transparent',
                          fillOpacity: 0,
                          weight: 12,
                          opacity: 0.6,
                          dashArray: '1 12',
                          className: 'boundary-glow',
                        }}
                        speed={14}
                      />
                      <MarchingPolygon
                        positions={positions}
                        pathOptions={{
                          color,
                          fillColor: color,
                          fillOpacity: 0.08,
                          weight: 5,
                          opacity: 0.95,
                          dashArray: '1 12',
                        }}
                        speed={14}
                      >
                        {popup}
                      </MarchingPolygon>
                    </Fragment>
                  );
                }

                return (
                  <Polygon
                    key={geofence.id}
                    positions={positions}
                    pathOptions={{
                      color,
                      fillColor: color,
                      fillOpacity: 0.12,
                      weight: 3,
                      dashArray: type === 'TARGET' ? '6 6' : undefined,
                    }}
                  >
                    {popup}
                  </Polygon>
                );
              }

              // Point geofences (planned pins + field-dropped HIDEOUT/RV pins).
              if (typeof geofence.centerLat === 'number' && typeof geofence.centerLon === 'number') {
                const isFieldPin = FIELD_PIN_TYPES.includes(type);
                const emoji = GEOFENCE_EMOJI[type] || '📍';
                const center: [number, number] = [geofence.centerLat, geofence.centerLon];
                const radius = typeof geofence.radiusMeters === 'number' ? geofence.radiusMeters : null;

                return (
                  <Fragment key={geofence.id}>
                    {radius && radius > 0 && (
                      <Circle
                        center={center}
                        radius={radius}
                        pathOptions={{
                          color,
                          fillColor: color,
                          fillOpacity: 0.1,
                          weight: 2,
                          dashArray: isFieldPin ? '4 4' : undefined,
                        }}
                      />
                    )}
                    <Marker
                      position={center}
                      icon={createGeofencePinIcon(color, emoji, isFieldPin)}
                      zIndexOffset={isFieldPin ? 950 : 900}
                    >
                      <Popup>
                        <div className="p-1">
                          <div className="font-bold">{geofence.name}</div>
                          <div className="text-xs text-gray-500">
                            {type}
                            {isFieldPin ? ' • field-dropped' : ' • planned'}
                          </div>
                          {geofence.description && (
                            <div className="text-xs text-gray-600 mt-1">{geofence.description}</div>
                          )}
                          <div className="mt-1 text-[10px] font-mono text-gray-400">
                            📍 {geofence.centerLat.toFixed(6)}, {geofence.centerLon.toFixed(6)}
                          </div>
                        </div>
                      </Popup>
                    </Marker>
                  </Fragment>
                );
              }

              return null;
            })}

          {/* Movement trails */}
          {filters.showTrails &&
            filteredLocations.map((location) => (
              <MovementTrail key={`trail-${location.deviceId}`} deviceId={location.deviceId} />
            ))}

          {/* Platoon markers */}
          {filteredLocations.map((location) => (
            <PlatoonMarker
              key={location.deviceId}
              location={location}
              onClick={() => selectLocation(location.deviceId)}
            />
          ))}

          {/* Platoon travel path */}
          {platoonPath && platoonPath.length > 1 && (
            <Polyline positions={platoonPath} pathOptions={{ color: '#f59e0b', weight: 4, opacity: 0.8 }} />
          )}

          {/* SOS pulse circles (independent of phase) */}
          {filters.showSOS &&
            filteredSOSEvents
              .filter((sos) => sos?.status && ['TRIGGERED', 'ACKNOWLEDGED'].includes(sos.status))
              .map((sos) => (
                <Circle
                  key={`sos-${sos.id}`}
                  center={[sos.latitude, sos.longitude]}
                  radius={100}
                  pathOptions={{ color: '#ef4444', fillColor: '#ef4444', fillOpacity: 0.2, weight: 3 }}
                />
              ))}

          {/* Responder affinity (P6): dashed line from each responding platoon's
              live position to its SOS (read-only, blue en-route / green arrived). */}
          {filters.showSOS &&
            filteredSOSEvents
              .filter((sos) => sos?.status && ['TRIGGERED', 'ACKNOWLEDGED', 'RESPONDING'].includes(sos.status))
              .flatMap((sos) =>
                (sos.responders ?? [])
                  .map((r: any) => {
                    const loc = filteredLocations.find((l) => l.platoonId === r.responderId);
                    if (!loc) return null;
                    const arrived = !!r.arrivedAt;
                    return (
                      <Polyline
                        key={`resp-${sos.id}-${r.id}`}
                        positions={[[loc.latitude, loc.longitude], [sos.latitude, sos.longitude]]}
                        pathOptions={{
                          color: arrived ? '#00cc66' : '#3399ff',
                          weight: 2,
                          opacity: 0.85,
                          dashArray: arrived ? undefined : '6 6',
                        }}
                      />
                    );
                  })
                  .filter(Boolean)
              )}

          {/* Off-screen indicators */}
          <OffScreenIndicators locations={Array.from(locations.values())} />

          {/* Operational overlay (F-1/F-2/F-4): farmbases, targets, intent arrows */}
          {filters.showFarmbases && <FarmbaseLayer farmbases={farmbases} />}
          {filters.showTargets && <TargetsLayer targets={targets} />}
          {filters.showTargets && (
            <IntentArrows targets={targets} locations={filteredLocations} farmbases={farmbases} />
          )}

          {/* Command-center layers (Round 2) */}
          <BattleOverlay />
          <MgrsReadout />
          <Rangefinder />

          {/* Recenter button: re-arm auto-fit + fit ALL operational elements */}
          <MapCenterControl
            points={fitPoints}
            paddingBottomRight={fitPadBottomRight}
            onRecenter={() => {
              userInteractedRef.current = false;
            }}
          />
        </MapContainer>

        {/* Brigade crest — top-left badge, clear of the map-type buttons */}
        <BrigadeCrest />

        {/* Filter overlay (desktop; on mobile this lives in the Filters bottom sheet).
            Sits to the right of the brigade crest so the two never overlap. */}
        <div className="hidden lg:block absolute top-4 left-[4.75rem] z-[1100]">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={clsx(
              'px-4 py-2 rounded-lg flex items-center gap-2 transition-colors',
              'bg-gray-900/90 border border-gray-700 text-gray-300',
              showFilters && 'bg-blue-600 border-blue-500 text-white'
            )}
          >
            <FunnelIcon className="w-5 h-5" />
            Filters
          </button>

          {showFilters && (
            <div className="mt-2 p-4 bg-gray-900/95 border border-gray-700 rounded-lg min-w-[220px]">
              <MapFilterPanel filters={filters} onChange={setFilters} />
            </div>
          )}
        </div>

        {/* Location label from URL params */}
        {urlName && (
          <div className="absolute top-16 lg:top-4 right-2 lg:right-4 z-[1000] px-4 py-2 bg-command-accent text-command-bg font-semibold rounded-lg shadow-lg max-w-[70vw] truncate">
            📍 Viewing: {decodeURIComponent(urlName)}
          </div>
        )}

        {/* Map type selector — right of the crest on phones (clear of the crest +
            Rangefinder), centered on desktop */}
        <div className="absolute top-2 left-16 lg:top-4 lg:left-1/2 lg:-translate-x-1/2 z-[1000]">
          <MapTypeSelector mapType={mapType} onChange={setMapType} />
        </div>

        {/* Legend: phases + geofence types + SOS (desktop; mobile uses the Legend sheet) */}
        <div className="hidden lg:block absolute bottom-4 left-4 z-[1000] p-3 bg-gray-900/95 border border-gray-700 rounded-lg max-w-[260px]">
          <MapLegend />
        </div>

        {/* Selected platoon detail panel */}
        {selectedLoc && (
          <PlatoonDetailPanel
            location={selectedLoc}
            onClose={() => selectLocation(null)}
            onTogglePath={() => {
              if (selectedPlatoonKey) {
                if (selectedPlatoonId === selectedPlatoonKey) clearPlatoonPath();
                else fetchPlatoonPath(selectedPlatoonKey);
              }
            }}
            isPathShown={!!selectedPlatoonKey && selectedPlatoonId === selectedPlatoonKey}
            pathLoading={pathLoading}
          />
        )}
      </div>

      {/* Side Panel - Floating over map on right side (desktop) */}
      <div className="hidden lg:flex absolute right-4 top-4 bottom-4 w-80 flex-col gap-3 z-[1001]">
        {/* Platoons Panel - Top Half */}
        <div className="flex-1 min-h-0 bg-gray-900/90 backdrop-blur-md border border-gray-700/50 rounded-xl overflow-hidden">
          <PlatoonsPanel
            filteredLocations={filteredLocations}
            platoonCounts={platoonCounts}
            selectedLocation={selectedLocation}
            onSelectLocation={selectLocation}
            sosEvents={filteredSOSEvents}
            selectedPlatoonId={selectedPlatoonId}
            pathLoading={pathLoading}
            onShowPath={fetchPlatoonPath}
            onClearPath={clearPlatoonPath}
          />
        </div>

        {/* Alerts Panel - Bottom Half */}
        <div className="flex-1 min-h-0 bg-gray-900/90 backdrop-blur-md border border-gray-700/50 rounded-xl overflow-hidden">
          <AlertsPanel
            sosEvents={filteredSOSEvents}
            alerts={filteredAlerts}
            notifications={notifications}
            geofenceEvents={filteredGeofenceEvents}
          />
        </div>
      </div>

      {/* Mobile: bottom chip bar + one-at-a-time sheets; the map stays full-bleed and pannable */}
      <div className="lg:hidden">
        <div className="absolute bottom-2 inset-x-2 z-[1050] overflow-x-auto [-webkit-overflow-scrolling:touch]">
          <div className="flex gap-1.5 p-1 w-max mx-auto bg-gray-900/95 border border-gray-700 rounded-full shadow-lg">
            {(
              [
                { key: 'filters', label: 'Filters', badge: null },
                { key: 'platoons', label: 'Platoons', badge: filteredLocations.length },
                { key: 'alerts', label: 'Alerts', badge: mobileAlertCount },
                { key: 'legend', label: 'Legend', badge: null },
              ] as const
            ).map((c) => (
              <button
                key={c.key}
                onClick={() => setMobileSheet(mobileSheet === c.key ? null : c.key)}
                className={clsx(
                  'flex items-center gap-1.5 px-3 min-h-11 rounded-full text-xs font-semibold whitespace-nowrap touch-manipulation transition-colors',
                  mobileSheet === c.key ? 'bg-blue-600 text-white' : 'text-gray-300 hover:bg-gray-800'
                )}
              >
                {c.label}
                {c.badge != null && (
                  <span
                    className={clsx(
                      'px-1.5 py-0.5 rounded-full text-[10px] font-bold',
                      mobileSheet === c.key ? 'bg-white/20 text-white' : 'bg-gray-700 text-gray-200'
                    )}
                  >
                    {c.badge}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        <BottomSheet open={mobileSheet === 'filters'} onClose={() => setMobileSheet(null)} title="Map Filters">
          <MapFilterPanel filters={filters} onChange={setFilters} />
        </BottomSheet>

        <BottomSheet open={mobileSheet === 'platoons'} onClose={() => setMobileSheet(null)} title="Platoons">
          <PlatoonsPanel
            filteredLocations={filteredLocations}
            platoonCounts={platoonCounts}
            selectedLocation={selectedLocation}
            onSelectLocation={(id) => {
              selectLocation(id);
              setMobileSheet(null); // reveal the map + platoon detail
            }}
            sosEvents={filteredSOSEvents}
            selectedPlatoonId={selectedPlatoonId}
            pathLoading={pathLoading}
            onShowPath={fetchPlatoonPath}
            onClearPath={clearPlatoonPath}
            hideHeader
          />
        </BottomSheet>

        <BottomSheet
          open={mobileSheet === 'alerts'}
          onClose={() => setMobileSheet(null)}
          title="Alerts"
          titleExtra={
            <span className="px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-gray-700 text-gray-200">
              {mobileAlertCount}
            </span>
          }
        >
          <AlertsPanel
            sosEvents={filteredSOSEvents}
            alerts={filteredAlerts}
            notifications={notifications}
            geofenceEvents={filteredGeofenceEvents}
            hideHeader
          />
        </BottomSheet>

        <BottomSheet open={mobileSheet === 'legend'} onClose={() => setMobileSheet(null)} title="Legend">
          <MapLegend />
        </BottomSheet>
      </div>
    </div>
  );
}
